import React from 'react';

import AnalyticsWebClient, { originType } from '@atlassiansox/analytics-web-client';
import { useIntl } from 'react-intl-next';
import type { EntryPointProps } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import { fg } from '@atlaskit/platform-feature-flags';
import { token, useThemeObserver } from '@atlaskit/tokens';
import {
	type EMBEDDED_CONFLUENCE_MODE,
	EmbeddedConfluence,
	Page,
} from '@atlassian/embedded-confluence';
import { themeStateObjectToQueryString } from '@atlassian/embedded-confluence-common';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { type PARENT_PRODUCTS } from '../../common/constants';
import { setHostnameForContent } from '../../utils/custom-domains';

import { messages } from './messages';
import { appendThemeParam, getAnalyticsEnv, onEditMode } from './utils';

export interface ConfluencePagePreviewPanelProps {
	parentProduct: (typeof PARENT_PRODUCTS)[keyof typeof PARENT_PRODUCTS];
	url: string;
}

export type ConfluencePagePreviewPanelEntrypointProps = EntryPointProps<
	Record<string, never>,
	Record<string, never>,
	ConfluencePagePreviewPanelProps,
	Record<string, never>
>;

const styles = cssMap({
	body: {
		flexGrow: 1,
		overflowY: 'auto',
		paddingInlineStart: token('space.300'),
		paddingBlockStart: token('space.300'),
	},
});

const ConfluencePagePreviewPanel = ({
	props: { url, parentProduct },
}: ConfluencePagePreviewPanelEntrypointProps) => {
	const { close, setPanelWidth } = usePreviewPanelsActions();
	const [analyticsClient, setAnalyticsClient] = React.useState<AnalyticsWebClient>();
	const { formatMessage } = useIntl();
	/*
		If the user is on a custom domain, we need to update the
		hostname to reflect the parentProduct's hostname. By providing the hostname
		prop we will force EP to use the parentProduct's hostname instead of the content's hostname
		when on a custom domain (see EP code here https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/platform/packages/confluence/embedded-confluence-common/src/page/parsePageProps.ts#75).
	*/
	const contentHostname = setHostnameForContent(url);
	const themeState = useThemeObserver();
	const themeStateParam = themeState
		? `themeState=${themeStateObjectToQueryString(themeState)}`
		: '';
	const formattedUrl = themeStateParam ? appendThemeParam(url, themeStateParam) : url;

	React.useEffect(() => {
		setAnalyticsClient(
			new AnalyticsWebClient({
				product: 'confluencePreviewPanel',
				env: getAnalyticsEnv(window.location?.hostname),
				origin: originType.WEB,
			}),
		);
	}, []);

	const onModeChange = (mode: EMBEDDED_CONFLUENCE_MODE) => onEditMode(mode, setPanelWidth);

	const onClose = () => {
		close();
	};

	const PageComponent = fg('embedded-confluence-spa-transition') ? EmbeddedConfluence : Page;

	return (
		<PreviewPanel testId="confluence-preview-panel">
			<PreviewPanelHeader
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				onClose={onClose}
				url={formattedUrl}
			>
				<PreviewPanelTitle
					title={formatMessage(messages.pageTitle)}
					icon={<PageIcon label="" size="medium" />}
				/>
			</PreviewPanelHeader>
			<PreviewPanelBody
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				styles={styles.body}
			>
				<PageComponent
					url={formattedUrl}
					showFooter={false}
					hasFooterLogo={false}
					onModeChange={onModeChange}
					parentProduct={parentProduct}
					embeddedConfluenceSource="confluence-page-preview-panel"
					hostname={contentHostname}
					themeState={themeState}
				/>
			</PreviewPanelBody>
		</PreviewPanel>
	);
};

export default ConfluencePagePreviewPanel;
